/**
 * LEGACY-ONLY. Renderiza documentos con design_version='legacy' (anteriores al
 * diseño lowcode). Los documentos nuevos se renderizan con TemplateRenderer
 * (motor lowcode). No usar para documentos nuevos. Ver PDF/TemplateEngine/resolveDocDesign.js.
 */
/* eslint-disable no-unused-vars */
import { Document, Page, StyleSheet, Text, View, Font } from '@react-pdf/renderer';
import React, { useEffect } from 'react';
import VoucherData from '../VoucherData/VoucherData';
import PageA4 from '../../Elements/Pages/PageA4';
import Container from '../../Elements/Some/Container';
import Image from '../../Elements/Some/Image';
import { API_PATH, PATH_GET_FILES } from '../../../config';
import Footer from '../../Footer/Footer';
import SignatureBlock from 'PDF/Signatures';
import { usePDFStyle } from 'providers/PDF/PDFStyleProvider';
import Header from 'PDF/Header';
import useFiles from 'hooks/useFiles';

const styles = StyleSheet.create({
  page: {
    paddingBottom: '4cm',
    paddingTop: '4cm',
    paddingHorizontal: '1.5cm',
    backgroundColor: '#ffffff',
    minHeight: '100vh',
  },
  section: {
    marginBottom: '1.5cm',
    padding: '0.8cm',
    backgroundColor: '#ffffff',
    borderRadius: '0.3cm',
    border: '1px solid #e8e8e8',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  body: {
    flex: 1,
    paddingTop: '0.5cm',
  },
  titleContainer: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    backgroundColor: '#548235',
    paddingVertical: '0.6cm',
    paddingHorizontal: '1cm',
    textAlign: 'center',
    borderRadius: '0.3cm',
    marginBottom: '1cm',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  paragraph: {
    fontSize: 11,
    fontFamily: 'Helvetica',
    color: '#34495e',
    lineHeight: 1.6,
    textAlign: 'justify',
    marginBottom: '0.8cm',
  },
  header: {
    width: '100vw',
    position: 'absolute',
    top: '0px',
    left: '0px',
    right: '0px',
    height: '3.5cm',
    backgroundColor: '#ffffff',
    borderBottom: '2px solid #548235',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: '1.5cm',
    paddingVertical: '0.4cm',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  footer: {
    position: 'absolute',
    bottom: '0px',
    left: '0px',
    right: '0px',
    width: '100vw',
    height: '3.5cm',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: '0.6cm',
    boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
  },
});

function VoucherDocument(props) {
  const { voucher, config = {}, images = [] } = props;
  const { getDownloadUrl } = useFiles();

  // Desactivar hyphenation para este documento específico
  useEffect(() => {
    // Guardar el callback original
    const originalCallback = Font.getHyphenationCallback();

    // Registrar un callback que no corte palabras
    Font.registerHyphenationCallback((word) => {
      return [word]; // Retorna la palabra completa sin cortar
    });

    // Restaurar el callback original cuando el componente se desmonte
    return () => {
      if (originalCallback) {
        Font.registerHyphenationCallback(originalCallback);
      }
    };
  }, []);

  return (
    <Document>
      <PageA4 style={styles.page}>
        {/* Header fijo */}
        <View style={styles.header} fixed>
          <Header
            title={`Comprobante de Atención N°${voucher?.id}`}
            config={config}
            date={voucher?.date}
          />
        </View>

        {/* Contenido principal */}
        <View style={styles.body}>
          {/* Sección 1: Datos del Comprobante */}
          <View style={styles.section}>
            <View style={styles.titleContainer}>
              <Text>Datos del Comprobante</Text>
            </View>
            <VoucherData
              recepcionist_name={voucher?.recepcionist_name}
              client_name={voucher?.client_name}
              reason={voucher?.reason}
              attention_init={voucher?.attention_init}
              attention_finish={voucher?.attention_finish}
              client_rut={voucher?.client_rut}
              equipment={voucher?.equipment}
              specialist_name={voucher?.specialist_name}
              date={voucher?.date}
              finish_state={voucher?.finish_state}
            />
          </View>

          {/* Sección 2: Observaciones */}
          <View style={styles.section} break>
            <View style={styles.titleContainer}>
              <Text>Observaciones</Text>
            </View>
            <Text style={styles.paragraph}>{voucher?.observations ?? 'SIN OBSERVACIONES'}</Text>
          </View>

          {/* Sección 3: Hallazgos detectados */}
          {voucher?.hallazgos?.some((h) => h.mostrar_en_comprobante) && (
            <View style={styles.section}>
              <View style={styles.titleContainer}>
                <Text>Hallazgos detectados</Text>
              </View>
              {voucher.hallazgos
                .filter((h) => h.mostrar_en_comprobante)
                .map((h, i) => (
                  <View key={i} style={{ marginBottom: '0.4cm' }}>
                    <Text style={styles.paragraph}>
                      {`[${h.tipo}${h.severidad ? ' · ' + h.severidad : ''}${
                        h.ascensor_nombre ? ' · ' + h.ascensor_nombre : ''
                      }] ${h.descripcion}`}
                    </Text>
                  </View>
                ))}
            </View>
          )}

          {/* Sección 4: Imágenes Adjuntas */}
          {images?.length > 0 && (
            <View style={styles.section}>
              <View style={styles.titleContainer}>
                <Text>Imágenes Adjuntas</Text>
              </View>
              <Container wrap>
                {images?.map(({ image, fileId }) => (
                  // El obtener la imagen por image está deprecado, es para la compatibilidad antes de usar S3
                  <Image
                    key={image}
                    source={fileId ? getDownloadUrl(fileId) : `${PATH_GET_FILES}/image/${image}`}
                  />
                ))}
              </Container>
            </View>
          )}

          {/* Sección 5: Información de Entrega */}
          <View style={[styles.section]} wrap={false}>
            <View style={styles.titleContainer}>
              <Text>Información de Entrega</Text>
            </View>
            <Text style={styles.paragraph}>
              A continuación, se encuentra la firma, la información del especialista y de quien
              recibe la atención. Esto, cuenta como una constancia firmada de que se realizó la
              atención.
            </Text>
            <SignatureBlock
              specialistSignature={voucher.specialist_signature}
              recepcionistName={voucher.recepcionist_name}
              recepcionistSignature={voucher.recepcionist_signature}
              recepcionistCharge={voucher.recepcionist_charge}
              specialistName={voucher.specialist_name}
            />
          </View>
        </View>

        {/* Footer fijo */}
        <View style={styles.footer} fixed>
          <Footer config={config} />
        </View>
      </PageA4>
    </Document>
  );
}

export default VoucherDocument;
